/**
 * The Story chronology grammar the web shares with Asset (register-story-observed-at.ts) and the
 * team's server (brain_team_story_contract.py): a strict ISO day or timestamp in years 1 to 9999, or
 * a day before year 1 as ISO 8601 writes it. That is a signed six-digit year with year 0 for 1 BC:
 * "-000752-04-21" is 21 April 753 BC. A day before year 1 is a calendar day only, and "-000000" is
 * refused: it is not ISO, and V8 reads it as 2001.
 *
 * Import-free, so node's type-stripping test runner can load it.
 */

const STRICT_STORY_TIMESTAMP =
  /^[0-9]{4}-[0-9]{2}-[0-9]{2}(?:T(?:[01][0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9](?:\.[0-9]{1,6})?)?(?:Z|[+-](?:[01][0-9]|2[0-3]):[0-5][0-9])?)?$/;

/** A day before year 1: "-000752-04-21" (753 BC), or "+000000-06-01" (1 BC). */
export const STORY_DAY_BEFORE_YEAR_ONE = /^(?:-(?!000000)[0-9]{6}|\+000000)-[0-9]{2}-[0-9]{2}$/;

const STORY_CALENDAR_DAY = /^([+-][0-9]{6}|[0-9]{4})-([0-9]{2})-([0-9]{2})/;

/** Whether a story date starts with a real calendar day, in any year. Date.parse rolls 2026-02-30
 * into March, and the first ten characters of a signed year are not its day. */
export function storyCalendarDayIsReal(value: string): boolean {
  const match = STORY_CALENDAR_DAY.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** A Story observed_at in epoch milliseconds, the one ordering key for 2024 and 753 BC alike; null
 * for anything outside the shared grammar or naming a day that does not exist. */
export function storyObservedAtMs(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  if (!STRICT_STORY_TIMESTAMP.test(value) && !STORY_DAY_BEFORE_YEAR_ONE.test(value)) return null;
  if (!storyCalendarDayIsReal(value)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}
